/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { CookieGroup, DEFAULT_COOKIE_GROUPS } from "privacy/CookieGroup";
import Analytics from "constants/Analytics";
import { NullRelic } from "./NullRelic";
import type NewRelic from "newrelic";
import { loadCustomerPersonalization } from "api/CustomerPersonalization";

/*
 * Adobe Consent Management Platform (CMP) Integration
 * Product: https://wiki.corp.adobe.com/pages/viewpage.action?pageId=2162941594
 * Engineering: https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=privacyatadobe&title=2020+CMP+Engineering+Implementation
 */
declare global {
  interface Window {
    newrelic: typeof NewRelic;
    localErrorHandler: (error: Error) => boolean;
    loadNewRelic: () => void;
    adobePrivacy?: {
      activeCookieGroups: () => CookieGroup[];
    };
  }
}

const isTest = JSON.parse(process.env.IS_TEST ?? "false");
const PRIVACY_JS_LIB_URL = process.env.PRIVACY_JS_LIB_URL;
const urlParams = new URLSearchParams(window.location.search);
const isUsingCustomPrivacyLocation = !!urlParams.get("customPrivacyLocation");

export function configureFromPrivacySettings(): void {
  const defaultNewRelicLoading =
    !isTest || localStorage.getItem("default-newrelic-loading") === "true";

  if (!isTest && !window.adobePrivacy) {
    console.error("Adobe CMP integration not loaded.");
  }
  const activeGroups =
    window.adobePrivacy?.activeCookieGroups() ?? DEFAULT_COOKIE_GROUPS;
  if (!defaultNewRelicLoading) {
    window.newrelic = new NullRelic() as any;
    Analytics.instance.setEventHandler(Analytics.loggingHandler);
  } else if (activeGroups.includes(CookieGroup.C0002)) {
    if (
      window.newrelic !== undefined &&
      window.newrelic?.constructor.name !== "NullRelic"
    ) {
      return;
    }
    window.newrelic = undefined as any;
    console.log("Loading NewRelic");
    window.loadNewRelic();
    window.newrelic.addRelease("Podcast", VERSION);
    Analytics.instance.setEventHandler(Analytics.loggingHandler);

    loadCustomerPersonalization();
  } else {
    console.log("Loading NullRelic");
    window.newrelic = new NullRelic() as any;
    Analytics.instance.setEventHandler(Analytics.nullHandler);
  }
}

export function injectPrivacyScript(): Promise<void> {
  const isSnapshot = navigator.userAgent === "ReactSnap";
  const isTestSafe = !isTest || isUsingCustomPrivacyLocation;
  const includeScript = isTestSafe && !isSnapshot;
  if (includeScript && PRIVACY_JS_LIB_URL) {
    const script = document.createElement("script");

    script.src = PRIVACY_JS_LIB_URL;

    document.body.appendChild(script);

    return new Promise((resolve) => {
      script.onload = (): void => {
        configureFromPrivacySettings();
        resolve();
      };
    });
  } else {
    configureFromPrivacySettings();
    return Promise.resolve();
  }
}

export function registerPrivacyEventListeners(): void {
  addEventListener("adobePrivacy:PrivacyConsent", configureFromPrivacySettings);
  addEventListener("adobePrivacy:PrivacyCustom", configureFromPrivacySettings);
  addEventListener("adobePrivacy:PrivacyReject", configureFromPrivacySettings);
}
